import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { PRODUCT_STATUS_TONE } from '@/lib/product-list';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PRODUCT_BLOCK_LIMITS,
  productBlocksText,
  type OptionValueInput,
  type ProductAttributeValueInput,
  type ProductBlockInput,
  type ProductDetailDto,
  type ProductKind,
  type ProductStatus,
} from '@ecom/contracts';
import { useCatalogStore, type ProductInput } from '@/stores/catalog.store';
import { useAttributesStore } from '@/stores/attributes.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { brandOptions, categoryOptions, sizeChartOptions } from '@/lib/options';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { fabricsOf } from '@/lib/fabrics';
import { adminApi, withSession } from '@/lib/api-client';
import { blocksPlainText, descriptionPayload, newBlock, readBlocks } from '@/lib/description-blocks';
import { AsyncSelect } from '@/components/async-select';
import { BilingualField, Choices, TextField, ToggleField, type BilingualValue } from '@/components/fields';
import { ProductPicker } from '@/components/product-picker';
import { AlertIcon, ArchiveIcon, CheckIcon, CopyIcon } from '@/components/icons';
import { DetailLayout, KeyValue, Page, SaveBar, Section, Tabs, Thumb, type TabItem } from '@/components/page';
import { Alert, Button, Card, ConfirmDialog, EmptyState, Field, Input, Pill, Select, Skeleton } from '@/components/ui';
import { OptionsEditor } from './options';
import { VariantsCard } from './variants';
import { VariantBuilder, type BuiltVariants } from './variant-builder';
import { VideosCard } from './videos';
import { ImagesCard } from './images';
import { SpecificationsCard } from './specifications';
import { CustomerInputsCard } from './customer-inputs';
import { ShippingCard } from './shipping';
import { DigitalCard } from './digital';
import { DescriptionEditor } from './description';

/**
 * Create and edit a product — one screen for both, because the fields are
 * identical and a separate "new" flow is a second place for validation to
 * drift.
 *
 * Laid out the way the reference dashboards lay out a resource: the product in
 * a wide column, split into tabs by what an operator came to do (its name and
 * price, its page's description, its variants, its pictures, its facts, how it
 * ships, how search reads it); and beside it, on every tab, what describes it — status, where it is
 * filed, and the figures at a glance. The product's own fields save from one
 * bar at the foot of the window; variants, photos and the rest save where they
 * are edited, because each is its own record.
 *
 * Prices are typed in riyals and converted to halalas exactly once, on the
 * digit string, by `lib/price-input` (ADR-004).
 */

type Tab = 'general' | 'description' | 'variants' | 'media' | 'specs' | 'inputs' | 'shipping' | 'seo';
const TABS: Tab[] = ['general', 'description', 'variants', 'media', 'specs', 'inputs', 'shipping', 'seo'];

type MeasureChoice = '' | 'm' | 'kg' | 'l';

export function ProductEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { current, currentLoading, error, open, clearCurrent } = useCatalogStore();
  const t = useT();

  const locale = useUiStore((state) => state.locale);

  // Re-read on a language change: names and option labels come resolved for the reader.
  useEffect(() => {
    if (isNew) clearCurrent();
    else void open(id);
  }, [id, isNew, open, clearCurrent, locale]);

  if (!isNew && (!current || current.id !== id)) {
    return (
      <Page title={t('editProduct')} back={{ to: '/products', label: t('products') }} width="wide">
        {currentLoading || !error ? (
          <DetailLayout main={<Skeleton className="h-[32rem]" />} aside={<Skeleton className="h-72" />} />
        ) : (
          <Alert>{error}</Alert>
        )}
      </Page>
    );
  }

  // Keyed by the product, so opening another one starts its form afresh; a
  // variant saved on this one does not.
  return <ProductForm key={isNew ? 'new' : current?.id} product={isNew ? null : current} />;
}

/** The product's own fields as the form holds them. */
interface Values {
  name: BilingualValue;
  /** The line beside the price (ADR-055). */
  shortDescription: BilingualValue;
  /** The plain long description: what the theme lays out while there are no blocks. */
  description: BilingualValue;
  /** The long description's blocks as typed; null is the theme's standard layout (ADR-055). */
  blocks: ProductBlockInput[] | null;
  basePrice: string;
  /** A parcel, files or a code (ADR-077). */
  kind: ProductKind;
  measureUnit: MeasureChoice;
  showsQuantityGuide: boolean;
  relatedIds: string[];
  status: ProductStatus;
  categoryId: string;
  /** Other categories it is listed in (R3); the primary is not among them. */
  alsoIn: { id: string; name: string }[];
  brandId: string;
  /** Its own size chart, over its category's (ADR-072); '' for none. */
  sizeChartId: string;
  /** Pre-order (ADR-079): on, the date it ships from (`YYYY-MM-DD`), and the most a variant may owe ('' = no limit). */
  preorderOn: boolean;
  preorderShipsOn: string;
  preorderLimit: string;
  metaTitle: BilingualValue;
  metaDescription: BilingualValue;
  /** Kept out of search engines (ADR-095). */
  noindex: boolean;
  /** Left out of the product feeds (ADR-096). */
  excludeFromFeeds: boolean;
  /** Days after delivery to remind a buyer to buy it again (ADR-099); empty for none. */
  repurchaseDays: string;
  tags: string;
  /** The address: `/products/{slug}`. Empty on a new product — the API makes one from the name. */
  slug: string;
}

function valuesFrom(product: ProductDetailDto | null): Values {
  // Both languages exactly as stored: the resolved `name` is whatever the
  // admin's language reads, and filling from it erased a language on save (R0).
  const content = product?.content;
  return {
    name: { en: content?.name.en ?? '', ar: content?.name.ar ?? '' },
    shortDescription: { en: content?.shortDescription?.en ?? '', ar: content?.shortDescription?.ar ?? '' },
    description: { en: content?.description.en ?? '', ar: content?.description.ar ?? '' },
    // A new product is written in blocks from the start; a saved one without
    // blocks keeps the theme's layout until someone lays it out (ADR-055).
    blocks: product ? readBlocks(content?.descriptionBlocks) : [newBlock('text')],
    // The base price, not `price`: that is the cheapest variant's, and saving
    // it as the base quietly repriced every variant that inherits.
    basePrice: product ? halalasToRiyals(product.basePrice) : '',
    kind: product?.kind ?? 'PHYSICAL',
    measureUnit: product?.measureUnit ?? '',
    showsQuantityGuide: product?.showsQuantityGuide ?? false,
    relatedIds: product?.relatedProductIds ?? [],
    status: product?.status ?? 'DRAFT',
    categoryId: product?.categoryId ?? '',
    alsoIn: product ? product.categories.filter((category) => category.id !== product.categoryId) : [],
    brandId: product?.brand?.id ?? '',
    sizeChartId: product?.sizeChartId ?? '',
    preorderOn: Boolean(product?.preorder),
    preorderShipsOn: product?.preorder?.shipsOn ?? '',
    preorderLimit: product?.preorder?.limit ? String(product.preorder.limit) : '',
    metaTitle: { en: content?.metaTitle.en ?? '', ar: content?.metaTitle.ar ?? '' },
    metaDescription: { en: content?.metaDescription.en ?? '', ar: content?.metaDescription.ar ?? '' },
    noindex: product?.noindex ?? false,
    excludeFromFeeds: product?.excludeFromFeeds ?? false,
    repurchaseDays: product?.repurchaseDays ? String(product.repurchaseDays) : '',
    tags: product?.tags.join(', ') ?? '',
    slug: product?.slug ?? '',
  };
}

/**
 * The payload the values make — also what "has anything changed" compares, so
 * the form as loaded and the form as typed go through it alike: blocks tidied
 * the way the API stores them, and `hadBlocks` from the same loaded product.
 */
function payloadFrom(values: Values, productId: string | null, hadBlocks: boolean): ProductInput | null {
  const price = riyalsToHalalas(values.basePrice);
  if (price === null) return null;
  const pair = (value: BilingualValue) =>
    value.en.trim()
      ? { en: { name: value.en.trim() }, ...(value.ar.trim() ? { ar: { name: value.ar.trim() } } : {}) }
      : undefined;
  const metaTitle = pair(values.metaTitle);
  const metaDescription = pair(values.metaDescription);
  // Blocks write the plain description themselves, so it is sent only without them (ADR-055).
  const layout = descriptionPayload(values.description, values.blocks, hadBlocks);
  const copy = (language: 'en' | 'ar') => ({
    name: values.name[language].trim(),
    shortDescription: values.shortDescription[language].trim() || null,
    ...(layout.description ? { description: layout.description[language] } : {}),
  });

  return {
    translations: {
      en: copy('en'),
      ...(values.name.ar.trim() ? { ar: copy('ar') } : {}),
    },
    ...('descriptionBlocks' in layout ? { descriptionBlocks: layout.descriptionBlocks } : {}),
    basePrice: price,
    kind: values.kind,
    measureUnit: values.measureUnit || null,
    // The guide is for things sold by measure; by the piece it has nothing to say.
    showsQuantityGuide: values.measureUnit ? values.showsQuantityGuide : false,
    relatedProductIds: values.relatedIds.filter((relatedId) => relatedId !== productId),
    status: values.status,
    categoryId: values.categoryId || null,
    // Every listing, primary first; the API keeps the primary listed (R3).
    categoryIds: [...(values.categoryId ? [values.categoryId] : []), ...values.alsoIn.map((category) => category.id)],
    brandId: values.brandId || null,
    sizeChartId: values.sizeChartId || null,
    // Pre-order is for parcels (ADR-079); a date puts it on, null takes it off.
    preorderShipsOn: values.kind === 'PHYSICAL' && values.preorderOn && values.preorderShipsOn ? values.preorderShipsOn : null,
    preorderLimit:
      values.kind === 'PHYSICAL' && values.preorderOn && /^[1-9][0-9]{0,5}$/.test(values.preorderLimit.trim()) ? Number(values.preorderLimit.trim()) : null,
    // Sent only when written: an empty meta title means "use the name".
    ...(metaTitle ? { metaTitle } : {}),
    ...(metaDescription ? { metaDescription } : {}),
    tags: values.tags
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean),
    // Sent only when written: the API makes a new product's address from its name.
    ...(values.slug.trim() ? { slug: slugOf(values.slug) } : {}),
    noindex: values.noindex,
    excludeFromFeeds: values.excludeFromFeeds,
    repurchaseDays: /^[1-9][0-9]{0,2}$/.test(values.repurchaseDays.trim()) ? Number(values.repurchaseDays.trim()) : null,
  };
}

/** An address as the storefront spells one: Latin, lower-case, dashes. */
function slugOf(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function ProductForm({ product }: { product: ProductDetailDto | null }) {
  const isNew = product === null;
  const { saving, error, create, update, archive, duplicate } = useCatalogStore();
  const locale = useUiStore((state) => state.locale);
  const navigate = useNavigate();
  const t = useT();
  const [params, setParams] = useSearchParams();

  const initial = useMemo(() => valuesFrom(product), [product]);
  const [values, setValues] = useState<Values>(initial);
  const set = <K extends keyof Values>(key: K, value: Values[K]) => setValues((current) => ({ ...current, [key]: value }));

  const [built, setBuilt] = useState<BuiltVariants>({ variants: [], optionValues: [], complete: false });
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  /*
   * What the tabs collected before the product existed — held in a ref, not
   * state: nothing renders from it, and re-rendering the editor on every tick
   * of another tab would throw away the focus someone was typing into.
   */
  const pending = useRef<Pending>({ specifications: [], fieldIds: [], imageKeys: [] });

  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'general';
  const setTab = (next: Tab) =>
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (next === 'general') copy.delete('tab');
      else copy.set('tab', next);
      return copy;
    }, { replace: true });

  // Whether the loaded product has blocks: only then does a save switch it back to the standard layout.
  const hadBlocks = readBlocks(product?.content?.descriptionBlocks) !== null;
  const payload = payloadFrom(values, product?.id ?? null, hadBlocks);
  const dirty = isNew || JSON.stringify(payload) !== JSON.stringify(payloadFrom(initial, product?.id ?? null, hadBlocks));
  const changed = !isNew && dirty;

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!changed) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [changed]);

  // ⌘S / Ctrl S saves, as it does in every editor an operator already uses.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        document.querySelector<HTMLFormElement>('#product-form')?.requestSubmit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    setFormError(null);

    if (!values.name.en.trim()) {
      setFormError(t('bilingualHint'));
      setTab('general');
      return;
    }
    if (!payload) {
      setFormError(t('basePriceHint'));
      setTab('general');
      return;
    }
    if (values.kind === 'PHYSICAL' && values.preorderOn && !values.preorderShipsOn) {
      setFormError(t('preorderDateRequired'));
      return;
    }

    if (isNew) {
      if (built.variants.length === 0 || !built.complete) {
        setFormError(t('variantHint'));
        setTab('variants');
        return;
      }
      const created = await create({ ...payload, variants: built.variants });
      if (!created) return;
      // Everything the other tabs collected attaches now that there is an id.
      const failed = await applyPending(created.id, { ...pending.current, optionValues: built.optionValues });
      if (failed) setFormError(failed);
      navigate(`/products/${created.id}`, { replace: true });
      return;
    }

    await update(product.id, payload);
  }

  const tabs: TabItem<Tab>[] = [
    { id: 'general', label: t('tabGeneral') },
    {
      id: 'description',
      label: t('tabDescription'),
      count: values.blocks && values.blocks.length > 0 ? count(values.blocks.length, locale) : undefined,
    },
    { id: 'variants', label: t('variants'), count: product ? count(product.variants.length, locale) : undefined },
    { id: 'media', label: t('tabMedia'), count: product ? count(product.images.length, locale) : undefined },
    { id: 'specs', label: t('tabSpecs') },
    { id: 'inputs', label: t('tabInputs') },
    // Physical facts belong to a variant, and a new product's variants are not rows yet. A digital
    // product has none: the tab is what it delivers instead (ADR-077).
    { id: 'shipping', label: values.kind === 'PHYSICAL' ? t('tabShipping') : t('tabDigital'), muted: isNew },
    { id: 'seo', label: t('tabSeo') },
  ];

  const title = product ? product.name : t('newProduct');

  return (
    <Page
      title={title}
      back={{ to: '/products', label: t('products') }}
      meta={
        product ? (
          <>
            <Pill tone={PRODUCT_STATUS_TONE[product.status]}>{t(`status${product.status}`)}</Pill>
            <code dir="ltr" className="text-xs text-ink-faint">
              /{product.slug}
            </code>
          </>
        ) : null
      }
      actions={
        product ? (
          <>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={async () => {
                const copy = await duplicate(product.id);
                if (copy) navigate(`/products/${copy.id}`);
              }}
            >
              <CopyIcon className="size-4" />
              {t('duplicate')}
            </Button>
            {product.status !== 'ARCHIVED' ? (
              <Button variant="secondary" onClick={() => setConfirmArchive(true)}>
                <ArchiveIcon className="size-4" />
                {t('archive')}
              </Button>
            ) : null}
          </>
        ) : null
      }
      tabs={<Tabs items={tabs} value={tab} onChange={setTab} label={title} />}
      width="wide"
    >
      <form id="product-form" onSubmit={submit} className="contents">
        <DetailLayout
          main={
            <>
              <div className={tab === 'general' ? 'contents' : 'hidden'}>
                <Section title={t('productBasics')} subtitle={t('bilingualHint')}>
                  <div className="flex flex-col gap-4">
                    <BilingualField label={t('productName')} value={values.name} onChange={(name) => set('name', name)} />
                    <BilingualField
                      label={t('shortDescription')}
                      hint={t('shortDescriptionHint')}
                      value={values.shortDescription}
                      onChange={(shortDescription) => set('shortDescription', shortDescription)}
                      multiline
                      rows={2}
                      limit={PRODUCT_BLOCK_LIMITS.shortDescription}
                    />
                  </div>
                </Section>

                {/* What arrives when it is bought (ADR-077). */}
                <Section title={t('productKind')} subtitle={product && values.kind !== product.kind ? t('productKindChanged') : undefined}>
                  <Choices
                    legend={t('productKind')}
                    legendHidden
                    name="product-kind"
                    columns={2}
                    value={values.kind}
                    onChange={(kind) => set('kind', kind)}
                    options={[
                      { value: 'PHYSICAL', label: t('kindPhysical'), hint: t('kindPhysicalHint') },
                      { value: 'DOWNLOAD', label: t('kindDownload'), hint: t('kindDownloadHint') },
                      { value: 'CODE', label: t('kindCode'), hint: t('kindCodeHint') },
                      // The store's own gift card (ADR-082): its variants are the amounts.
                      { value: 'GIFT_CARD', label: t('kindGiftCard'), hint: t('kindGiftCardHint') },
                    ]}
                  />
                </Section>

                <Section title={t('pricing')}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t('basePrice')} htmlFor="basePrice" hint={t('basePriceHint')}>
                      <Input
                        id="basePrice"
                        dir="ltr"
                        inputMode="decimal"
                        value={values.basePrice}
                        invalid={Boolean(values.basePrice.trim()) && riyalsToHalalas(values.basePrice) === null}
                        onChange={(event) => set('basePrice', event.target.value)}
                        required
                      />
                    </Field>
                    <Field label={t('soldBy')} htmlFor="measureUnit" hint={t('soldByHint')}>
                      <Select
                        id="measureUnit"
                        value={values.measureUnit}
                        onChange={(event) => set('measureUnit', event.target.value as MeasureChoice)}
                        className="w-full"
                      >
                        <option value="">{t('soldByPiece')}</option>
                        <option value="m">{t('unit_m')}</option>
                        <option value="kg">{t('unit_kg')}</option>
                        <option value="l">{t('unit_l')}</option>
                      </Select>
                    </Field>
                    {values.measureUnit ? (
                      <div className="sm:col-span-2">
                        <ToggleField
                          label={t('showsQuantityGuide')}
                          hint={t('showsQuantityGuideHint')}
                          checked={values.showsQuantityGuide}
                          onChange={(next) => set('showsQuantityGuide', next)}
                        />
                      </div>
                    ) : null}
                  </div>
                </Section>

                {/* What goes with it (R15): shown on its page and in the basket. */}
                <Section title={t('goesWith')} subtitle={t('goesWithHint')}>
                  <ProductPicker
                    ordered
                    label={t('goesWithPicked')}
                    selectedIds={values.relatedIds}
                    onChange={(ids) => set('relatedIds', ids.filter((relatedId) => relatedId !== product?.id).slice(0, 12))}
                  />
                </Section>
              </div>

              <div className={tab === 'description' ? 'contents' : 'hidden'}>
                <DescriptionEditor
                  product={product}
                  name={values.name}
                  shortDescription={values.shortDescription}
                  description={values.description}
                  blocks={values.blocks}
                  alsoIn={values.alsoIn.map((category) => category.name)}
                  onDescription={(description) => set('description', description)}
                  onBlocks={(blocks) => set('blocks', blocks)}
                  onTab={setTab}
                />
              </div>

              <div className={tab === 'variants' ? 'contents' : 'hidden'}>
                {product ? (
                  <>
                    <OptionsEditor product={product} />
                    <VariantsCard product={product} />
                  </>
                ) : (
                  <VariantBuilder
                    nameEn={values.name.en}
                    measureUnit={values.measureUnit}
                    basePrice={values.basePrice}
                    onChange={setBuilt}
                  />
                )}
              </div>

              <div className={tab === 'media' ? 'contents' : 'hidden'}>
                {product ? (
                  <>
                    <ImagesCard product={product} />
                    <VideosCard product={product} />
                  </>
                ) : (
                  <ImagesCard onDraftChange={(keys) => (pending.current.imageKeys = keys)} />
                )}
              </div>

              <div className={tab === 'specs' ? 'contents' : 'hidden'}>
                {product ? (
                  <SpecificationsCard productId={product.id} />
                ) : (
                  // Driven by the category chosen beside, since there is no product to read a template from.
                  <SpecificationsCard
                    categoryId={values.categoryId || null}
                    onDraftChange={(next) => (pending.current.specifications = next)}
                  />
                )}
              </div>

              <div className={tab === 'inputs' ? 'contents' : 'hidden'}>
                {product ? (
                  <CustomerInputsCard productId={product.id} />
                ) : (
                  <CustomerInputsCard onDraftChange={(ids) => (pending.current.fieldIds = ids)} />
                )}
              </div>

              <div className={tab === 'shipping' ? 'contents' : 'hidden'}>
                {product && values.kind !== product.kind ? (
                  // Files and codes hang on what the product is once saved.
                  <Card className="flex flex-col">
                    <EmptyState title={t('saveFirst')} body={t('digitalSaveKindFirst')} />
                  </Card>
                ) : product && product.kind === 'GIFT_CARD' ? (
                  // Nothing to upload or paste: a card is issued per unit when it is paid for (ADR-082).
                  <Card className="flex flex-col">
                    <EmptyState title={t('kindGiftCard')} body={t('giftCardProductHint')} />
                  </Card>
                ) : product && product.kind !== 'PHYSICAL' ? (
                  <DigitalCard product={product} />
                ) : product ? (
                  <ShippingCard product={product} />
                ) : (
                  <Card className="flex flex-col">
                    <EmptyState title={t('saveFirst')} body={t('shippingPendingHint')} />
                  </Card>
                )}
              </div>

              <div className={tab === 'seo' ? 'contents' : 'hidden'}>
                <SearchPreview values={values} fallbackSlug={product?.slug ?? slugOf(values.name.en)} />
                <Section title={t('tabSeo')} subtitle={t('seoHint')}>
                  <div className="flex flex-col gap-4">
                    <Field
                      label={t('urlHandle')}
                      htmlFor="slug"
                      hint={product && values.slug !== product.slug ? t('urlHandleChanged') : t('urlHandleHint')}
                    >
                      <div dir="ltr" className="flex items-center overflow-hidden rounded-control border border-line bg-surface focus-within:border-brand-500">
                        <span dir="ltr" className="shrink-0 bg-surface-sunken px-3 py-2 text-sm text-ink-faint">
                          /products/
                        </span>
                        <input
                          id="slug"
                          dir="ltr"
                          value={values.slug}
                          placeholder={slugOf(values.name.en)}
                          onChange={(event) => set('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                          className="min-h-10 min-w-0 flex-1 bg-transparent px-2 font-mono text-sm text-ink outline-none"
                        />
                      </div>
                    </Field>
                    <BilingualField
                      label={t('metaTitle')}
                      hint={t('metaTitleHint')}
                      value={values.metaTitle}
                      onChange={(next) => set('metaTitle', next)}
                    />
                    <BilingualField
                      label={t('metaDescription')}
                      value={values.metaDescription}
                      onChange={(next) => set('metaDescription', next)}
                      multiline
                      rows={3}
                    />
                    <ToggleField
                      label={t('seoHideLabel')}
                      hint={t('seoHideProductHint')}
                      checked={values.noindex}
                      onChange={(next) => set('noindex', next)}
                    />
                    <ToggleField
                      label={t('feedsExcludeLabel')}
                      hint={t('feedsExcludeHint')}
                      checked={values.excludeFromFeeds}
                      onChange={(next) => set('excludeFromFeeds', next)}
                    />
                    <TextField
                      label={t('repurchaseDaysLabel')}
                      hint={t('repurchaseDaysHint')}
                      type="number"
                      inputMode="numeric"
                      latin
                      value={values.repurchaseDays}
                      onChange={(next) => set('repurchaseDays', next)}
                    />
                  </div>
                </Section>
              </div>
            </>
          }
          aside={
            <>
              <Section title={t('productStatus')}>
                <Select
                  id="status"
                  aria-label={t('productStatus')}
                  value={values.status}
                  onChange={(event) => set('status', event.target.value as ProductStatus)}
                  className="w-full"
                >
                  <option value="DRAFT">{t('statusDRAFT')}</option>
                  <option value="ACTIVE">{t('statusACTIVE')}</option>
                  <option value="ARCHIVED">{t('statusARCHIVED')}</option>
                </Select>
                <p className="mt-2 text-xs text-ink-muted">{t(`status${values.status}Hint`)}</p>
              </Section>

              <Section title={t('organization')}>
                <div className="flex flex-col gap-4">
                  <Field label={t('category')} htmlFor="categoryId">
                    <AsyncSelect
                      id="categoryId"
                      value={values.categoryId}
                      emptyLabel={t('noCategory')}
                      fetchPage={categoryOptions.page}
                      fetchOne={categoryOptions.one}
                      onChange={(categoryId) =>
                        setValues((current) => ({
                          ...current,
                          categoryId,
                          // The primary is always listed, so it is not also an extra.
                          alsoIn: current.alsoIn.filter((row) => row.id !== categoryId),
                        }))
                      }
                    />
                  </Field>

                  <Field label={t('alsoListedIn')} htmlFor="alsoIn" hint={t('alsoListedInHint')}>
                    <div className="flex flex-col gap-2">
                      {values.alsoIn.length > 0 ? (
                        <ul className="flex flex-wrap gap-1.5">
                          {values.alsoIn.map((category) => (
                            <li
                              key={category.id}
                              className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-sunken ps-3 text-sm text-ink"
                            >
                              {category.name}
                              <button
                                type="button"
                                aria-label={t('removeFrom').replace('{name}', category.name)}
                                onClick={() => set('alsoIn', values.alsoIn.filter((row) => row.id !== category.id))}
                                className="inline-flex size-7 items-center justify-center rounded-full text-ink-muted hover:text-ink"
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <AsyncSelect
                        id="alsoIn"
                        value=""
                        emptyLabel={t('addCategory')}
                        fetchPage={async (search, cursor) => {
                          const page = await categoryOptions.page(search, cursor);
                          const taken = new Set([values.categoryId, ...values.alsoIn.map((row) => row.id)]);
                          return { ...page, options: page.options.filter((option) => !taken.has(option.id)) };
                        }}
                        onChange={(categoryId, option) => {
                          if (!categoryId || categoryId === values.categoryId) return;
                          setValues((current) => ({
                            ...current,
                            alsoIn: current.alsoIn.some((row) => row.id === categoryId)
                              ? current.alsoIn
                              : [...current.alsoIn, { id: categoryId, name: option?.label ?? categoryId }],
                          }));
                        }}
                      />
                    </div>
                  </Field>

                  <Field label={t('brand')} htmlFor="brand" hint={t('brandHint')}>
                    <AsyncSelect
                      id="brand"
                      value={values.brandId}
                      emptyLabel={t('noBrand')}
                      fetchPage={brandOptions.page}
                      fetchOne={brandOptions.one}
                      onChange={(brandId) => set('brandId', brandId)}
                    />
                  </Field>

                  {/* "Which size am I?" (ADR-072) — empty shows its category's chart, if any. */}
                  <Field label={t('sizeChart')} htmlFor="size-chart" hint={t('productSizeChartHint')}>
                    <AsyncSelect
                      id="size-chart"
                      value={values.sizeChartId}
                      emptyLabel={t('sizeChartFromCategory')}
                      fetchPage={sizeChartOptions.page}
                      fetchOne={sizeChartOptions.one}
                      onChange={(sizeChartId) => set('sizeChartId', sizeChartId)}
                    />
                  </Field>

                  <Field label={t('tags')} htmlFor="tags" hint={t('tagsHint')}>
                    <Input id="tags" dir="ltr" value={values.tags} onChange={(event) => set('tags', event.target.value)} />
                  </Field>
                </div>
              </Section>

              {/* Selling what has not arrived yet (ADR-079) — parcels only. */}
              {values.kind === 'PHYSICAL' ? (
                <Section title={t('preorder')} subtitle={t('preorderHint')}>
                  <div className="flex flex-col gap-4">
                    <ToggleField label={t('preorderOn')} checked={values.preorderOn} onChange={(on) => set('preorderOn', on)} />
                    {values.preorderOn ? (
                      <>
                        <Field label={t('preorderShipsOn')} htmlFor="preorder-ships" hint={t('preorderShipsOnHint')}>
                          <Input
                            id="preorder-ships"
                            type="date"
                            dir="ltr"
                            value={values.preorderShipsOn}
                            onChange={(event) => set('preorderShipsOn', event.target.value)}
                          />
                        </Field>
                        <Field label={t('preorderLimit')} htmlFor="preorder-limit" hint={t('preorderLimitHint')}>
                          <Input
                            id="preorder-limit"
                            dir="ltr"
                            inputMode="numeric"
                            value={values.preorderLimit}
                            onChange={(event) => set('preorderLimit', event.target.value.replace(/[^0-9]/g, ''))}
                          />
                        </Field>
                      </>
                    ) : null}
                  </div>
                </Section>
              ) : null}

              {product ? <Readiness product={product} onTab={setTab} /> : null}
              {product ? <AtAGlance product={product} /> : null}
            </>
          }
        />
      </form>

      <SaveBar
        saving={saving}
        dirty={dirty}
        form="product-form"
        saveLabel={isNew ? t('newProduct') : undefined}
        message={formError || error ? <Alert>{formError ?? error}</Alert> : undefined}
        onDiscard={!isNew && dirty ? () => setValues(initial) : undefined}
      />

      <ConfirmDialog
        open={confirmArchive}
        title={t('archiveTitle')}
        body={t('archiveBody')}
        confirmLabel={t('archive')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (product && (await archive(product.id))) navigate('/products');
          setConfirmArchive(false);
        }}
        onCancel={() => setConfirmArchive(false)}
      />
    </Page>
  );
}

/** The figures an operator checks before anything else: picture, price, how much is left. */
function AtAGlance({ product }: { product: ProductDetailDto }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const colours = fabricsOf(product).filter((fabric) => fabric.color !== null).length;
  const stock = product.variants.reduce((sum, variant) => sum + variant.stock, 0);
  const lead = [...product.images].sort((a, b) => a.sortOrder - b.sortOrder)[0];

  return (
    <Section title={t('atAGlance')}>
      <div className="flex items-center gap-3">
        <Thumb src={mediaUrl(lead?.url)} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{product.name}</p>
          <p data-numeric className="text-sm text-ink-muted">
            {money(product.price, locale, product.currency)}
          </p>
        </div>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3">
        <KeyValue label={t('variants')}>
          <span data-numeric>{count(product.variants.length, locale)}</span>
        </KeyValue>
        <KeyValue label={t('colours')}>
          <span data-numeric>{count(colours, locale)}</span>
        </KeyValue>
        <KeyValue label={t('totalStock')}>
          <span data-numeric className={stock === 0 ? 'text-danger' : undefined}>
            {count(stock, locale)}
          </span>
        </KeyValue>
        <KeyValue label={t('images')}>
          <span data-numeric>{count(product.images.length, locale)}</span>
        </KeyValue>
      </dl>
    </Section>
  );
}

/**
 * How the product reads in a search result — the reference dashboards' search
 * engine listing preview — with the lengths search engines cut at. Falls back
 * to the name, then the short description, then the long one's words, as the
 * storefront does (ADR-055).
 */
function SearchPreview({ values, fallbackSlug }: { values: Values; fallbackSlug: string }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const pick = (value: BilingualValue) => (locale === 'ar' ? value.ar || value.en : value.en || value.ar);
  const title = pick(values.metaTitle) || pick(values.name);
  // With blocks the plain description is theirs, as the API will derive it.
  const plain = values.blocks ? blocksPlainText(values.blocks) : values.description;
  const description = pick(values.metaDescription) || pick(values.shortDescription) || pick(plain);
  const slug = values.slug.trim() || fallbackSlug;

  return (
    <Section title={t('searchPreview')} subtitle={t('searchPreviewHint')}>
      <div className="rounded-control border border-line bg-surface p-4">
        <p dir="ltr" className="truncate text-xs text-ink-muted">
          …/products/{slug || '…'}
        </p>
        <p className="mt-1 line-clamp-1 text-lg text-[#1a0dab] dark:text-[#8ab4f8]">{title || t('productName')}</p>
        <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{description || '—'}</p>
      </div>
      <p data-numeric className="mt-2 text-xs text-ink-faint">
        {t('searchLengths', { title: count(title.length, locale), description: count(description.length, locale) })}
      </p>
    </Section>
  );
}

/**
 * What stands between the product and selling well, each a jump to where it
 * is fixed. Advice, not a gate: a draft can be saved half done, and the API
 * refuses only what would be wrong, not what is merely missing (ADR-053).
 */
function Readiness({ product, onTab }: { product: ProductDetailDto; onTab: (tab: Tab) => void }) {
  const t = useT();
  // The specifications tab has loaded this product's questions; the ones its
  // category requires and it has not answered are named here (ADR-053).
  const { productSlots, productSlotsFor } = useAttributesStore();
  const unanswered =
    productSlotsFor === product.id
      ? productSlots.filter((slot) => slot.isRequired && slot.isActive !== false && slot.display === '').length
      : 0;
  const content = product.content;
  // Laid out, the page's words are its blocks'; otherwise the plain description (ADR-055).
  const blocks = readBlocks(content?.descriptionBlocks);
  const described = blocks
    ? productBlocksText(blocks, 'en') !== null && productBlocksText(blocks, 'ar') !== null
    : Boolean(content?.description.en && content?.description.ar);
  const colours = fabricsOf(product).filter((fabric) => fabric.color !== null);
  const sellable = product.variants.filter((variant) => !variant.isSample);
  const checks: { ok: boolean; label: string; tab: Tab }[] = [
    { ok: Boolean(content?.name.ar && content?.name.en), label: t('readyNames'), tab: 'general' },
    { ok: Boolean(content?.shortDescription?.ar && content?.shortDescription?.en), label: t('readyShortDescription'), tab: 'general' },
    { ok: described, label: t('readyDescriptions'), tab: 'description' },
    { ok: product.images.length > 0, label: t('readyPhoto'), tab: 'media' },
    ...(colours.length > 1
      ? [{ ok: colours.every((fabric) => fabric.images.length > 0), label: t('readyColourPhotos'), tab: 'media' as Tab }]
      : []),
    { ok: Boolean(product.categoryId), label: t('readyCategory'), tab: 'general' },
    {
      ok: unanswered === 0,
      label: unanswered === 0 ? t('readyRequiredSpecs') : t('readyRequiredSpecsMissing', { count: String(unanswered) }),
      tab: 'specs',
    },
    { ok: sellable.some((variant) => variant.stock > 0), label: t('readyStock'), tab: 'variants' },
    { ok: sellable.every((variant) => variant.price > 0), label: t('readyPrices'), tab: 'variants' },
    { ok: Boolean(content?.metaDescription.en || content?.metaDescription.ar), label: t('readySearch'), tab: 'seo' },
  ];
  const done = checks.filter((check) => check.ok).length;

  return (
    <Section title={t('readyTitle')} subtitle={t('readyCount', { done: String(done), total: String(checks.length) })}>
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
        <div className="h-full rounded-full bg-success transition-all" style={{ width: `${(done / checks.length) * 100}%` }} />
      </div>
      <ul className="flex flex-col gap-1">
        {checks.map((check) => (
          <li key={check.label}>
            <button
              type="button"
              onClick={() => onTab(check.tab)}
              className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-start text-sm transition-colors hover:bg-surface-sunken"
            >
              {check.ok ? (
                <CheckIcon className="size-4 shrink-0 text-success" />
              ) : (
                <AlertIcon className="size-4 shrink-0 text-warning" />
              )}
              <span className={check.ok ? 'text-ink-muted' : 'text-ink'}>{check.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** What the tabs collected before the product had an id to attach it to. */
interface Pending {
  specifications: ProductAttributeValueInput[];
  fieldIds: string[];
  imageKeys: string[];
  optionValues?: OptionValueInput[];
}

/**
 * Attaches what the tabs buffered to the product that now exists.
 *
 * Returns a message when part of it failed rather than throwing: the product
 * was created, and losing that because an image did not attach would be worse
 * than an operator saving one tab again.
 */
async function applyPending(productId: string, pending: Pending): Promise<string | null> {
  const failures: string[] = [];

  if (pending.optionValues && pending.optionValues.length > 0) {
    if (!(await useCatalogStore.getState().setOptionValues(productId, pending.optionValues))) failures.push('colours');
  }

  if (pending.specifications.length > 0) {
    try {
      await withSession(() => adminApi.attributes.setForProduct(productId, pending.specifications));
    } catch {
      failures.push('specifications');
    }
  }

  if (pending.fieldIds.length > 0) {
    try {
      await withSession(() =>
        adminApi.productFields.setForProduct(
          productId,
          pending.fieldIds.map((fieldId) => ({ fieldId })),
        ),
      );
    } catch {
      failures.push('inputs');
    }
  }

  for (const imageKey of pending.imageKeys) {
    try {
      await withSession(() => adminApi.catalog.addImage(productId, { imageKey }));
    } catch {
      failures.push('images');
      break;
    }
  }

  return failures.length > 0 ? `Saved, but could not attach: ${failures.join(', ')}.` : null;
}
